/* eslint-disable no-template-curly-in-string */
import {
  DEBOUNCE_WAIT_FOR_SORT,
  DEFAULT_PRIMARY_BATCH_SIZE,
} from '../../constants/statics'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import update from 'immutability-helper'
import _ from 'lodash'
import { Table, Column, Cell } from 'fixed-data-table-2'
// @ts-ignore no types
import ReactResizeDetector from 'react-resize-detector'
import classnames from 'classnames/bind'
import {
  CheckboxField,
  Dropdown,
  Icon,
  Search,
  withApplicationNotificationsContext,
} from '@adc/ui-core'
import pluralize from 'pluralize'
import { formatNumber } from '../../utils/formatters'
import CheckboxCell from './cells/checkboxCell'
import ActionCell from './cells/actionCell'
import BoolCell from './cells/boolCell'
import CurrencyCell from './cells/currencyCell'
import DateCell from './cells/dateCell'
import FilterCell from './cells/filterCell'
import HeaderCell from './cells/headerCell'
import HtmlCell from './cells/htmlCell'
import LabelCell from './cells/labelCell'
import NumberCell from './cells/numberCell'
import PlaceholderCell from './cells/placeholderCell'
import StatusCell from './cells/statusCell'
import TextCell from './cells/textCell'
import TimeCell from './cells/timeCell'
import ValueToTextCell from './cells/valueToTextCell'
import ClauseHolder from './clauseHolder'
import SavedSearches from './savedSearches'
import SearchField from './searchField'
import DataTableUtils from './utils'
import BulkProcessingModule from './bulkProcessingModule'
import BulkUpdateDrawer from './bulkUpdateDrawer'
import ActionIcon from './actionIcon'
import EditDataColumnsModal, {
  EditDataColumnsMenuItem,
} from './menuOptions/editDataColumns'
import DownloadDataModal, { DownloadMenuItem } from './menuOptions/downloadData'
import {
  COLUMN_TYPES as _COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
} from './constants'

// keyMirrorList returns a plain object but TS can't infer its shape from the JS file
const COLUMN_TYPES = _COLUMN_TYPES as Record<string, string>
import defineTableSchema from './tableSchema'
import 'fixed-data-table-2/dist/fixed-data-table.css'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const ROW_HEIGHT = 35
const TOOLBAR_DEFAULT_HEIGHT = 48
const INFOBAR_DEFAULT_HEIGHT = 60 // includes some 8px padding
const searchRegExp = (searchFilter: string) =>
  new RegExp(searchFilter.replace(/\s/g, '.*'), 'ig')

/** Base type for row data items in the DataTable */
interface DataTableRowData {
  [key: string]: unknown
  selected?: boolean
  error?: boolean | string
  warning?: boolean | string
}

/** Column definition type (legacy format used in morpheus) */
interface ColumnDefinition {
  field: string
  type?: string
  title?: string
  colProps: {
    fixed?: boolean
    fixedRight?: boolean
    flexGrow?: number
    width?: number
    isResizable?: boolean
    isReorderable?: boolean
    isVisible?: boolean
    searchable?: boolean
    sort?: number
    sortAscending?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

interface FilterClause {
  values: string[]
  fields: string[]
  title: string
  valuesAsText: string
  dataType?: string
  partialMatchFields?: string[]
  searchOperation?: string
  setMatch?: boolean
  searchAnalyzer?: string
  sortAnalyzer?: string
}

interface BrowserStorage {
  getValue: (key: string) => unknown
  setValue: (key: string, value: unknown) => void
  removeValue?: (key: string) => void
}

interface SavedSearchesState {
  selectedSearchId?: string | null
  isDirty?: boolean
  searches?: unknown[]
  errorInfo?: unknown
  [key: string]: unknown
}

interface SavedSearchesActions {
  setSelectedSavedSearch: (id: string | null) => void
  setSavedSearchDirty: (isDirty: boolean) => void
  setSavedSearchDirtyColumns: (columns: unknown[]) => void
  fetchSavedSearches: () => void | Promise<void>
  putSavedSearch: () => void | Promise<void>
  postSavedSearch: (
    searchName: string,
    searchType: string
  ) => void | Promise<void>
  deleteSavedSearch: (id: string) => void | Promise<void>
}

interface DownloadData {
  data?: unknown[]
  isLoading?: boolean
  error?: Error | null
  [key: string]: unknown
}

interface DownloadActions {
  fetchData: () => void | Promise<void>
  abort: () => void
  reset: () => void
}

interface Suggestions {
  [key: string]: unknown[]
}

interface Notifications {
  toast?: {
    open: (config: {
      message?: string
      title?: string
      error?: unknown
      role?: string
    }) => void
  }
}

interface SortValue {
  field: string
  sortAscending: boolean
  dataType: string
}

interface ColumnReorderEvent {
  reorderColumn: string
  columnAfter?: string
}

interface MenuOptionItem {
  text: string
  value: string
  isDisabled?: boolean
}

interface BulkProcessingComponentProps {
  selectedRecords: unknown[]
  invoked: boolean
  onComplete: () => void
}

interface SearchFieldHandle {
  updateColumn: (column: string | null, options: { value: string }) => void
}

interface StandardToolbarProps {
  allowExportCSV: boolean
  refreshData: () => void
  isLoading: boolean
  enableLocalSearch: boolean
  enableSavedSearches: boolean
  savedSearches: SavedSearchesState
  filterClauses: FilterClause[]
  userAuthorities: string[]
  renderStandardSearch: () => React.ReactElement
  renderLocalSearch: () => React.ReactElement
  renderSavedSearch: () => React.ReactElement
  renderDataTableActions: () => React.ReactElement
}

interface DataTableProps {
  rowData: DataTableRowData[]
  columns: ColumnDefinition[]
  browserStorage?: BrowserStorage
  entityKey: string
  primaryKeyField: string
  changeSortBy?: (
    sortValues: SortValue[],
    columns: ColumnDefinition[]
  ) => void | Promise<void>
  fetchDataRowsAppend?: () => void | Promise<void>
  refreshData?: () => void
  isLoading?: boolean
  isNonPrimaryBatchLoading?: boolean
  totalCount?: number
  entityName?: string
  bulkProcessingModules?: BulkProcessingModule[]
  filterClauses?: FilterClause[]
  addFilter?: (clause: FilterClause) => void
  removeFilter?: (clause: FilterClause) => void
  clearFilters?: () => void
  onRowAction?: (action: string, rowData: unknown) => void
  onRowToggleAction?: (rowIndex: number) => void
  onScrollEnd?: () => void
  customTableHeader?: () => React.ReactNode
  customToolbar?: React.ComponentType<StandardToolbarProps>
  suggestions?: Suggestions
  userAuthorities?: string[]
  updateSuggestions?: (suggestions: Suggestions) => void
  allowExportCSV?: boolean
  downloadData?: DownloadData
  downloadActions?: DownloadActions
  highlightErrorsAndWarnings?: boolean
  minCharsForAsyncSuggestions?: number
  enableLocalSearch?: boolean
  enableSavedSearches?: boolean
  savedSearches?: SavedSearchesState
  savedSearchesActions?: SavedSearchesActions
  customFormatters?: Record<string, (value: unknown) => string>
  enableOperationSelection?: boolean
  primaryBatchSize?: number
  notifications?: Notifications
  /** When true, the root container becomes a flex column child that fills
   *  its parent's remaining height. Use inside flex-column layouts where
   *  the DataTable should stretch to fill available space. */
  fill?: boolean
}

// Stable default values for optional object/array props.
// Inline literals (= {}, = []) in destructuring create new references on every
// render, which triggers useEffect/useCallback dependency changes and causes
// infinite re-render loops. Module-level constants keep a single stable reference.
const DEFAULTS = {
  rowData: [] as DataTableRowData[],
  bulkProcessingModules: [] as BulkProcessingModule[],
  filterClauses: [] as FilterClause[],
  suggestions: {} as Suggestions,
  userAuthorities: [] as string[],
  downloadData: {} as DownloadData,
  downloadActions: {
    fetchData: _.noop,
    abort: _.noop,
    reset: _.noop,
  } as DownloadActions,
  savedSearches: {} as SavedSearchesState,
  savedSearchesActions: {
    setSelectedSavedSearch: _.noop,
    setSavedSearchDirty: _.noop,
    setSavedSearchDirtyColumns: _.noop,
    fetchSavedSearches: _.noop,
    putSavedSearch: _.noop,
    postSavedSearch: _.noop,
    deleteSavedSearch: _.noop,
  } as SavedSearchesActions,
  customFormatters: {} as Record<string, (value: unknown) => string>,
  notifications: {} as Notifications,
}

const DataTable: React.FC<DataTableProps> = ({
  rowData: rowDataProp = DEFAULTS.rowData,
  columns: columnsProp,
  browserStorage,
  entityKey,
  primaryKeyField,
  changeSortBy = _.noop,
  fetchDataRowsAppend = _.noop,
  refreshData = _.noop,
  isLoading = false,
  isNonPrimaryBatchLoading,
  totalCount = 0,
  entityName,
  bulkProcessingModules = DEFAULTS.bulkProcessingModules,
  filterClauses = DEFAULTS.filterClauses,
  addFilter = _.noop,
  removeFilter: removeFilterProp = _.noop,
  clearFilters: clearFiltersProp = _.noop,
  onRowAction = _.noop,
  onRowToggleAction = _.noop,
  onScrollEnd = _.noop,
  customTableHeader,
  customToolbar: CustomToolbar,
  suggestions = DEFAULTS.suggestions,
  userAuthorities = DEFAULTS.userAuthorities,
  updateSuggestions = _.noop,
  allowExportCSV = false,
  downloadData = DEFAULTS.downloadData,
  downloadActions = DEFAULTS.downloadActions,
  highlightErrorsAndWarnings = true,
  minCharsForAsyncSuggestions = 1,
  enableLocalSearch = false,
  enableSavedSearches = false,
  savedSearches = DEFAULTS.savedSearches,
  savedSearchesActions = DEFAULTS.savedSearchesActions,
  customFormatters = DEFAULTS.customFormatters,
  enableOperationSelection = false,
  primaryBatchSize = DEFAULT_PRIMARY_BATCH_SIZE,
  notifications = DEFAULTS.notifications,
  fill,
}) => {
  const setLastNonFixedColumnToFlexGrow = useCallback(
    (columns: ColumnDefinition[]) => {
      const lastNonFixed = _.findLast(
        columns,
        (column: ColumnDefinition) =>
          !(column.colProps.fixedRight || column.colProps.fixed)
      )

      const newColumns = columns.map((column: ColumnDefinition) => {
        const newCol = _.cloneDeep(column)
        newCol.colProps.flexGrow =
          lastNonFixed && newCol.field === lastNonFixed.field ? 1 : 0
        return newCol
      })

      return newColumns
    },
    []
  )

  // Refs that mirror props so stable useCallback closures always read latest
  // values — the functional equivalent of `this.props` in the class component.
  const savedSearchesRef = useRef(savedSearches)
  savedSearchesRef.current = savedSearches
  const savedSearchesActionsRef = useRef(savedSearchesActions)
  savedSearchesActionsRef.current = savedSearchesActions
  const browserStorageRef = useRef(browserStorage)
  browserStorageRef.current = browserStorage

  // State initialization (from constructor)
  // Single object avoids two renders from ResizeObserver (non-React event)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [toolbarHeight, setToolbarHeight] = useState(TOOLBAR_DEFAULT_HEIGHT)
  const [allChecked, setAllChecked] = useState(false)
  const [columnsState, setColumnsState] = useState<ColumnDefinition[]>(() =>
    setLastNonFixedColumnToFlexGrow(
      DataTableUtils.getAllVisibleColumns(
        browserStorage,
        savedSearches,
        entityKey,
        columnsProp
      )
    )
  )
  const [filterColumns, setFilterColumns] = useState<ColumnDefinition[]>(
    () => DataTableUtils.getSearchableColumns(columnsProp) as ColumnDefinition[]
  )
  const [rowData, setRowData] = useState<DataTableRowData[]>(rowDataProp)
  const [editColumnsModalOpen, setEditColumnsModalOpen] = useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const [bulkInvoked, setBulkInvoked] = useState(false)
  const [bulkModuleId, setBulkModuleId] = useState<string | null>(null)
  const [selectedRecords, setSelectedRecords] = useState<unknown[]>([])
  const [, setIsCSVDataReady] = useState(false)
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null)

  const searchField = useRef<SearchFieldHandle | null>(null)

  // Refs for tracking previous prop values (UNSAFE_componentWillUpdate replacement)
  const prevRowDataRef = useRef(rowDataProp)
  const prevColumnsRef = useRef(columnsProp)
  const prevSavedSearchesErrorRef = useRef<unknown>(null)

  /**
   * Takes the current columns state and stores it to dirtyColumns.
   */
  const updateDirtyColumns = useCallback((cols: ColumnDefinition[]) => {
    savedSearchesActionsRef.current.setSavedSearchDirtyColumns(
      DataTableUtils.getDataColumns(cols)
    )
  }, [])

  const saveColumns = useCallback(
    (cols: ColumnDefinition[]) => {
      if (!_.get(savedSearchesRef.current, 'selectedSearchId')) {
        DataTableUtils.saveDataColumnsToBrowserStorage(
          browserStorageRef.current,
          entityKey,
          cols
        )
      }

      // always update dirty columns - even if no saved search currently selected - so we can save it later if needed.
      updateDirtyColumns(cols)
    },
    [entityKey, updateDirtyColumns]
  )

  // ── Render-time prop-to-state synchronization ──────────────────────────
  // The React-recommended replacement for UNSAFE_componentWillUpdate is to
  // call setState *during render*. React detects this, aborts the current
  // render, and immediately restarts with the new state — no extra render
  // pass, no flicker, and no useEffect/useLayoutEffect timing issues.

  // Track previous selectedSearchId for render-time comparison
  const prevSelectedSearchIdRef = useRef(savedSearches.selectedSearchId)
  // Flag to defer dirty-columns notification (side effect) to an effect
  const pendingDirtyColumnsRef = useRef<ColumnDefinition[] | null>(null)

  // Shared helper — computes visible columns from storage/saved-search and
  // applies the flexGrow rule to the last non-fixed column.
  const recomputeVisibleColumns = () =>
    setLastNonFixedColumnToFlexGrow(
      DataTableUtils.getAllVisibleColumns(
        browserStorage,
        savedSearches,
        entityKey,
        columnsProp
      )
    )

  // Handle rowData prop changes (render-time)
  if (rowDataProp !== prevRowDataRef.current) {
    prevRowDataRef.current = rowDataProp
    rowDataProp.forEach((record: DataTableRowData) => {
      if (typeof record === 'object')
        record.selected = selectedRecords.includes(record[primaryKeyField])
    })
    setRowData(rowDataProp)
    setAllChecked(false)
    setActiveRowIndex(null)
  }

  // Handle columns prop changes (render-time)
  if (columnsProp !== prevColumnsRef.current) {
    prevColumnsRef.current = columnsProp
    const newCols = recomputeVisibleColumns()
    setColumnsState(newCols)
    pendingDirtyColumnsRef.current = newCols
    // Also sync the selectedSearchId ref so the check below doesn't
    // redundantly recalculate columns in the same render.
    prevSelectedSearchIdRef.current = savedSearches.selectedSearchId
  }

  // Handle saved search selection changes (render-time)
  // When a saved search is selected/deselected, columnsProp reference may
  // not change, so we need a separate check on selectedSearchId.
  // NOTE: Do NOT set pendingDirtyColumnsRef here — selecting a saved search
  // loads its stored columns; they are not "dirty" (user-modified). Marking
  // them dirty would cause the parent to attempt a PUT that the API rejects.
  if (savedSearches.selectedSearchId !== prevSelectedSearchIdRef.current) {
    prevSelectedSearchIdRef.current = savedSearches.selectedSearchId
    const newCols = recomputeVisibleColumns()
    setColumnsState(newCols)
  }

  // Deferred side effect: notify parent of dirty columns after render.
  // (Cannot call parent actions during render, so this stays as an effect.)
  // No dependency array — intentional: the trigger is a ref mutation, not
  // a state/prop change, so we check the ref on every commit. The ref guard
  // makes it a no-op on renders where no column recalculation occurred.
  useEffect(() => {
    if (pendingDirtyColumnsRef.current) {
      const cols = pendingDirtyColumnsRef.current
      pendingDirtyColumnsRef.current = null
      updateDirtyColumns(cols)
    }
  })

  // Handle savedSearches error — only re-run when errorInfo changes, not on
  // every savedSearches mutation (e.g. isDirty toggling on resize/reorder).
  const savedSearchesErrorInfo = savedSearches.errorInfo
  useEffect(() => {
    if (!prevSavedSearchesErrorRef.current && savedSearchesErrorInfo) {
      notifyError(savedSearchesErrorInfo, 'Saved Search Error')
    }
    prevSavedSearchesErrorRef.current = savedSearchesErrorInfo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearchesErrorInfo])

  // Update filterColumns when columns prop changes
  useEffect(() => {
    setFilterColumns(
      DataTableUtils.getSearchableColumns(columnsProp) as ColumnDefinition[]
    )
  }, [columnsProp])

  const updateColumnProps = useCallback(
    (newColumnProps: Record<string, unknown>, columnKey: string) => {
      let updatedColumns: ColumnDefinition[] | null = null
      setColumnsState(prevColumns => {
        const index = _.findIndex(prevColumns, { field: columnKey })
        if (index !== -1) {
          const columnToUpdate = prevColumns[index]
          const newColumn = {
            ...columnToUpdate,
            colProps: {
              ...columnToUpdate.colProps,
              ...newColumnProps,
            },
          }

          updatedColumns = update(prevColumns, {
            $splice: [[index, 1, newColumn]],
          })

          return updatedColumns
        }
        return prevColumns
      })
      // Save synchronously after setState (not via setTimeout) so that
      // browserStorage is up-to-date before any parent re-render can trigger
      // the columns effect and read stale widths from storage.
      if (updatedColumns) {
        saveColumns(updatedColumns)
      }
    },
    [saveColumns]
  )

  const onColumnReorderEndCallback = useCallback(
    (event: ColumnReorderEvent) => {
      const reorderColumn = _.find(columnsState, {
        field: event.reorderColumn,
      })
      // TODO: may have to consider case where reorderColumn is not in existing columns (???)
      if (reorderColumn) {
        savedSearchesActionsRef.current.setSavedSearchDirty(true)
        const newColumns = _.filter(
          columnsState,
          (column: ColumnDefinition) => {
            return column.field !== event.reorderColumn
          }
        )

        if (event.columnAfter) {
          const index = _.findIndex(newColumns, { field: event.columnAfter })
          newColumns.splice(index, 0, reorderColumn)
        } else {
          newColumns.splice(columnsState.length - 1, 0, reorderColumn)
        }

        const updatedColumns = setLastNonFixedColumnToFlexGrow(newColumns)
        setColumnsState(updatedColumns)
        saveColumns(updatedColumns)
      }
    },
    [columnsState, setLastNonFixedColumnToFlexGrow, saveColumns]
  )

  const onColumnResizeEndCallback = useCallback(
    (newColumnWidth: number, columnKey: string) => {
      savedSearchesActionsRef.current.setSavedSearchDirty(true)
      updateColumnProps({ width: newColumnWidth }, columnKey)
    },
    [updateColumnProps]
  )

  // Refs so the stable debounced closure always reads latest values
  const sortDepsRef = useRef({
    changeSortBy,
    savedSearchesActions,
    columnsState,
    rowData,
  })
  sortDepsRef.current = {
    changeSortBy,
    savedSearchesActions,
    columnsState,
    rowData,
  }

  // useMemo keeps a single debounced instance across renders
  const onColumnSort = useMemo(
    () =>
      _.debounce(
        async (columnKey: string, sortAscending: boolean, dataType: string) => {
          const {
            changeSortBy: sort,
            savedSearchesActions: actions,
            columnsState: cols,
            rowData: rows,
          } = sortDepsRef.current
          actions.setSavedSearchDirty(true)
          // notify parent to give sorted rowData if given changeSortBy func, otherwise do client-side sort
          if (sort) {
            await sort([{ field: columnKey, sortAscending, dataType }], cols)
            setSelectedRecords([])
            setIsCSVDataReady(false)
          } else {
            const sortedRowData = _.orderBy(
              rows,
              columnKey,
              !sortAscending ? 'desc' : 'asc'
            )
            setRowData(sortedRowData)
            setSelectedRecords([])
            setIsCSVDataReady(false)
          }
          // reset previous sorted column for single field sort
          const latestCols = sortDepsRef.current.columnsState
          const index = _.findIndex(latestCols, { colProps: { sort: 1 } })
          if (index !== -1) {
            updateColumnProps(
              { sort: 0, sortAscending: undefined },
              latestCols[index].field
            )
          }
          // update current sorted column
          updateColumnProps({ sort: 1, sortAscending }, columnKey)
        },
        DEBOUNCE_WAIT_FOR_SORT
      ),
    [updateColumnProps] // stable dep — debounce instance survives re-renders
  )

  /**
   * Toggles all checkboxes. Assumes the checkbox field is called 'selected'
   */
  const toggleAll = async () => {
    const tableMax = totalCount || 0

    if (rowData.length === primaryBatchSize && rowData.length < tableMax) {
      await fetchDataRowsAppend()
    }
    // We destructure here because rowData might be different if the fetch call above happens
    const currentRowData = rowData
    const ids = currentRowData
      .filter((item: DataTableRowData) => !item.error)
      .map((item: DataTableRowData) => {
        item.selected = !allChecked
        return item[primaryKeyField]
      })

    setAllChecked(!allChecked)
    setSelectedRecords(!allChecked ? ids : [])
    setRowData([...currentRowData])
  }

  const toggle = useCallback(
    (rowIndex: number, field: string) => {
      const newRowData = [...rowData]
      newRowData[rowIndex] = { ...newRowData[rowIndex] }
      newRowData[rowIndex][field] = !newRowData[rowIndex][field]

      const newSelectedRecords = [...selectedRecords]
      if (field === 'selected') {
        const id = newRowData[rowIndex][primaryKeyField]
        if (newSelectedRecords.includes(id)) {
          _.remove(newSelectedRecords, item => item === id)
        } else {
          newSelectedRecords.push(id)
        }
      }

      setRowData(newRowData)
      setSelectedRecords(newSelectedRecords)
      onRowToggleAction?.(rowIndex)
    },
    [rowData, selectedRecords, primaryKeyField, onRowToggleAction]
  )

  const onRowClickHandler = useCallback(
    (event: React.SyntheticEvent, rowIndex: number) => {
      const target = event.target as HTMLElement
      const isActionClick =
        target.onclick || target.parentElement?.nodeName === 'A'

      // toggle active state as long as it's not a action click
      setActiveRowIndex(
        !isActionClick && activeRowIndex === rowIndex ? null : rowIndex
      )
    },
    [activeRowIndex]
  )

  const rowClassNameGetter = useCallback(
    (rowIndex: number | null | undefined) => {
      if (_.isNil(rowIndex)) return ''
      let className = ''

      if (rowIndex === activeRowIndex) {
        className = 'active'
      } else if (highlightErrorsAndWarnings && rowData[rowIndex]) {
        if (rowData[rowIndex].error) {
          className = 'error'
        } else if (rowData[rowIndex].warning) {
          className = 'warning'
        }
      }

      // Alternating revision shading for audit history tables
      const row = rowData[rowIndex]
      const revIdx = row && (row._revisionIndex as number | undefined)
      if (revIdx != null && revIdx % 2 === 1) {
        className = className
          ? `${className} revision-shaded`
          : 'revision-shaded'
      }

      return className
    },
    [activeRowIndex, highlightErrorsAndWarnings, rowData]
  )

  // returns a render function
  const renderStandardHeader = (definition: ColumnDefinition) => () =>
    <HeaderCell definition={definition} onSortChange={onColumnSort} />

  // returns a render function
  const renderTextCell =
    (defaultCellProps: {
      data: DataTableRowData[]
      definition: ColumnDefinition & { title: string }
      customFormatters?: Record<string, (value: unknown) => string>
    }) =>
    () =>
      <TextCell {...defaultCellProps} />

  // returns two render functions, one for header and one for cell
  const renderCheckboxColumn = (
    definition: ColumnDefinition,
    data: DataTableRowData[],
    checked: boolean
  ) => ({
    header: () => (
      <Cell className={cx('header', 'checkbox-cell')}>
        <CheckboxField
          checkboxLabel={'\x00'} // force semantic to pick up clicks outside checkbox but inside cell
          elmId='select_all'
          checked={checked}
          className={cx('checkbox-cell')}
          onChange={toggleAll}
        />
      </Cell>
    ),
    cell: () => (
      <CheckboxCell data={data} definition={definition} toggle={toggle} />
    ),
  })

  const getCell = (definition: ColumnDefinition, data: DataTableRowData[]) => {
    let cell
    const defaultCellProps = {
      data,
      definition: definition as ColumnDefinition & { title: string },
    }

    switch (definition.type) {
      case COLUMN_TYPES.STATUS:
        cell = () => <StatusCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.LABEL:
        cell = () => <LabelCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.HTML:
        cell = () => <HtmlCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.BOOL:
        cell = () => <BoolCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.NUMBER:
        cell = () => <NumberCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.CURRENCY:
        cell = () => <CurrencyCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.DATE:
        cell = () => <DateCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.FILTER:
        cell = () => <FilterCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.TIME:
        cell = () => <TimeCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.VALUE_TEXT:
        // ValueToTextCell requires `options` as a named prop on definition (PropTypes.array.isRequired)
        // ColumnDefinition's index signature satisfies this at runtime but not at compile time
        cell = () => (
          <ValueToTextCell
            data={data}
            definition={
              definition as ColumnDefinition & {
                title: string
                options: unknown[]
              }
            }
          />
        )
        break
      case COLUMN_TYPES.ACTION:
        cell = () => (
          <ActionCell
            onRowAction={onRowAction}
            primaryKeyField={primaryKeyField}
            userAuthorities={userAuthorities}
            {...defaultCellProps}
          />
        )
        break
      default:
        // always include customFormatters to be used when needed
        cell = renderTextCell({ ...defaultCellProps, customFormatters })
    }
    return cell
  }

  const renderColumn = (
    definition: ColumnDefinition,
    data: DataTableRowData[],
    checked: boolean,
    loading: boolean
  ) => {
    // filled with appropriate render functions below
    let header: (() => React.ReactElement) | undefined
    let cell: (() => React.ReactElement) | undefined
    const defaultCellProps = {
      data,
      definition: definition as ColumnDefinition & { title: string },
    }

    if (definition.type === COLUMN_TYPES.CHECKBOX) {
      ;({ header, cell } = renderCheckboxColumn(definition, data, checked))
    } else {
      /**
       * Typical columns go here that use default column header, but perhaps a different cell type
       */
      header = renderStandardHeader(definition)
      cell =
        loading &&
        definition.type !== COLUMN_TYPES.ACTION &&
        !isNonPrimaryBatchLoading
          ? () => <PlaceholderCell {...defaultCellProps} />
          : getCell(definition, data)
    }

    return (
      <Column
        key={definition.field}
        columnKey={definition.field}
        minWidth={40}
        header={header!()}
        cell={cell!()}
        {...(definition.colProps as Record<string, unknown> & {
          width: number
        })}
      />
    )
  }

  const handleColumnsVisibility = useCallback(
    (selectedColumns: string[]) => {
      /**
       * Along with update to the visibility, also reorder the columns the way they got added or removed in the modal.
       * Since columnsState maintains the current colProps, hence should be the main reference.
       */
      const nonDataColumns = DataTableUtils.getNonDataColumns(columnsProp)
      const reorderedDataColumns = selectedColumns.reduce(
        (newColumns: ColumnDefinition[], selectedColumn: string) => {
          const columnFoundInState = columnsState.find(
            (column: ColumnDefinition) => column.field === selectedColumn
          )
          if (!_.isEmpty(columnFoundInState)) {
            // column found in state always visible, hence simply add to array
            newColumns.push(columnFoundInState)
          } else {
            const columnFoundInProps = columnsProp.find(
              (column: ColumnDefinition) => column.field === selectedColumn
            )
            if (!_.isEmpty(columnFoundInProps)) {
              // column found in props MUST RESET its sort properties if defined as sortable
              // since we simply switch visibility here, no sort trigger
              newColumns.push(
                DataTableUtils.resetSortProps(columnFoundInProps, {
                  isVisible: true,
                }) as ColumnDefinition
              )
            }
          }
          return newColumns
        },
        []
      )

      const updatedColumns = setLastNonFixedColumnToFlexGrow([
        ...nonDataColumns,
        ...reorderedDataColumns,
      ])
      setColumnsState(updatedColumns)
      saveColumns(updatedColumns)
    },
    [columnsProp, columnsState, setLastNonFixedColumnToFlexGrow, saveColumns]
  )

  const renderEditColumnsModal = () => (
    /** allColumns: gives the original column configuration (should not come from any browser storage),
     * selectedColumns: gives the current visible columns (includes the fixed and visible
     * columns and may come from browser storage)
     **/
    <EditDataColumnsModal
      modalOpen={editColumnsModalOpen}
      closeModal={() => setEditColumnsModalOpen(false)}
      saveConfig={onSaveConfig}
      allColumns={columnsProp}
      selectedColumns={columnsState}
    />
  )

  const onSaveConfig = (selectedColumns: string[]) => {
    savedSearchesActions.setSavedSearchDirty(true)
    handleColumnsVisibility(selectedColumns)
    setEditColumnsModalOpen(false)
  }

  const renderDownloadModal = () => (
    <DownloadDataModal
      modalOpen={downloadModalOpen}
      closeModal={() => setDownloadModalOpen(false)}
      columns={columnsState}
      totalCount={totalCount}
      filename={`${entityKey.toLowerCase()}.csv`}
      notify={notify}
      downloadData={downloadData}
      downloadActions={downloadActions}
    />
  )

  const handleActionSelection = (
    _event: React.MouseEvent<HTMLDivElement>,
    data: { value: string }
  ) => {
    switch (data.value) {
      case EditDataColumnsMenuItem.value:
        _event.nativeEvent.stopImmediatePropagation()
        setEditColumnsModalOpen(true)
        break
      case DownloadMenuItem.value:
        _event.nativeEvent.stopImmediatePropagation()
        setDownloadModalOpen(true)
        break
      default:
        break
    }
  }

  const notify = (message: string) => {
    notifications.toast?.open({
      message,
    })
  }

  const notifyError = (err: unknown, title: string) => {
    // notify user
    const { toast } = notifications
    toast?.open({ title, error: err })
  }

  const isDuplicateClause = (filterClause: FilterClause) => {
    return _.findKey(
      filterClauses,
      (clause: FilterClause) =>
        _.isEqual(
          clause.values.map(_.toLower).sort(),
          filterClause.values.map(_.toLower).sort()
        ) && _.isEqual(clause.fields, filterClause.fields)
    )
  }

  const removeFilterFunc = (filterClause: FilterClause) => {
    setIsCSVDataReady(false)
    removeFilterProp(filterClause)
    updateDirtyColumns(columnsState)
  }

  // We duplicate some logic here to allow the notification system to work as it has.
  const onSearchFieldSubmit = (filterClause: FilterClause) => {
    const existingClauseIndex = DataTableUtils.getClauseIndex(
      filterClauses,
      filterClause
    )
    // We don't use _.compact here because we want to keep false values
    // @ts-ignore NaN isn't assignable to a string but preserving previous treatment
    if (_.isEmpty(_.without(filterClause.values, undefined, null, '', NaN))) {
      removeFilterFunc(filterClause)
      setSelectedRecords([])
      setIsCSVDataReady(false)
      existingClauseIndex !== -1 &&
        notify(`${filterClause.title} filter removed`)
    } else {
      if (!isDuplicateClause(filterClause)) {
        addFilter(filterClause)
        updateDirtyColumns(columnsState)
        notify(
          `${filterClause.title} filter ${
            existingClauseIndex === -1 ? 'added' : 'updated'
          }`
        )
        setSelectedRecords([])
        setIsCSVDataReady(false)
      } else {
        notify(
          `Filter [${filterClause.title}: ${filterClause.valuesAsText}] already applied`
        )
      }
    }
  }

  const resizeContainer = useCallback((width: number, height: number) => {
    setContainerSize({ width, height })
  }, [])

  const resizeToolbar = useCallback((_width: number, height: number) => {
    setToolbarHeight(height || TOOLBAR_DEFAULT_HEIGHT)
  }, [])

  const onSelectSavedSearch = (id: string | null) => {
    savedSearchesActions.setSelectedSavedSearch(id || null)
    searchField.current?.updateColumn(null, { value: '' }) // clear currently selected field in searchField
    _.defer(refreshData)
  }

  const onCreateSavedSearch = (searchName: string, savedSearchType: string) => {
    savedSearchesActions.postSavedSearch(searchName, savedSearchType)
  }

  const onUpdateSavedSearch = () => {
    savedSearchesActions.putSavedSearch()
  }

  const onDeleteSavedSearch = (id: string) => {
    // first, unselect whatever saved search is selected
    if (savedSearches.selectedSearchId) {
      onSelectSavedSearch(null)
    }
    // then blast it
    savedSearchesActions.deleteSavedSearch(id)
  }

  const searchNestedValues = (
    obj: Record<string, unknown>,
    searchFilter: string
  ): boolean => {
    const regex = searchRegExp(searchFilter)
    return Object.values(obj).some((val: unknown) => {
      if (typeof val === 'object' && val !== null) {
        // If the value is a nested object, search its values recursively
        return searchNestedValues(val as Record<string, unknown>, searchFilter)
      } else {
        // If the value is not an object, test it against the search filter
        return regex.test((val || '').toString())
      }
    })
  }

  const onLocalSearchChange = (e: React.SyntheticEvent<HTMLElement>) => {
    const searchFilter = (e.target as HTMLInputElement).value

    setRowData(
      !searchFilter
        ? rowDataProp
        : rowDataProp.filter((row: DataTableRowData) =>
            searchNestedValues(row as Record<string, unknown>, searchFilter)
          )
    )
  }

  const renderStandardSearch = () => {
    return (
      <SearchField
        ref={searchField as React.RefObject<never>}
        filterColumns={filterColumns}
        suggestions={suggestions}
        updateSuggestions={updateSuggestions}
        onSubmit={onSearchFieldSubmit}
        minCharsForAsyncSuggestions={minCharsForAsyncSuggestions}
        clauses={filterClauses}
        enableOperationSelection={enableOperationSelection}
      />
    )
  }

  const renderLocalSearch = () => (
    <Search
      className={cx('local-search')}
      placeholder='Search'
      onSearchChange={onLocalSearchChange}
      showNoResults={false}
    />
  )

  const renderSavedSearch = () => (
    <SavedSearches
      selectedSearch={_.get(savedSearches, 'selectedSearchId')}
      isDirty={_.get(savedSearches, 'isDirty')}
      searches={_.get(savedSearches, 'searches', [])}
      onCreate={onCreateSavedSearch}
      onUpdate={onUpdateSavedSearch}
      onDelete={onDeleteSavedSearch}
      onSelect={onSelectSavedSearch}
      isUserGlobalSearchManager={userAuthorities.includes('GSW')}
    />
  )

  const renderDataTableActions = () => {
    const options: MenuOptionItem[] = [EditDataColumnsMenuItem]

    if (allowExportCSV) {
      options.unshift(DownloadMenuItem)
      options[0].isDisabled = !totalCount
    }

    return (
      <>
        <ActionIcon
          data-elm-id='refresh'
          onClick={refreshData}
          name='refresh'
          loading={isLoading}
          size='small'
        />
        <Dropdown
          className={cx('right-dropdown')}
          icon={null}
          trigger={<ActionIcon name='ellipsis vertical' size='small' />}
          selectOnBlur={false}
          data-elm-id='dropdown_table_actions'
          position='right'
          closeOnChange
        >
          <Dropdown.Menu>
            {options.map((option: MenuOptionItem, index: number) => (
              <Dropdown.Item
                text={option.text}
                value={option.value}
                key={index}
                onClick={
                  handleActionSelection as (
                    event: React.MouseEvent<HTMLDivElement>,
                    data: Record<string, unknown>
                  ) => void
                }
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </>
    )
  }

  const renderToolbar = () => {
    return (
      <div className={cx('toolbar')}>
        {renderStandardSearch()}
        {enableLocalSearch && renderLocalSearch()}
        {enableSavedSearches && renderSavedSearch()}
        {renderDataTableActions()}
      </div>
    )
  }

  const renderTableHeaderInfo = (data: DataTableRowData[]) => {
    const anyCheckboxColumn = _.some(columnsState, {
      type: COLUMN_TYPES.CHECKBOX,
    })
    const countSelected = _.sumBy(data, (row: DataTableRowData) =>
      row.selected ? 1 : 0
    )
    return (
      <div className={cx('info')}>
        {anyCheckboxColumn && (
          <span>
            <strong data-elm-id='count_selected'>
              {formatNumber(countSelected as any)}
            </strong>
            {' of '}
          </span>
        )}
        <strong data-elm-id='total_count_client_side'>
          {formatNumber((totalCount || data.length) as any)}
        </strong>
        {anyCheckboxColumn
          ? ' Selected'
          : ` ${_.startCase(_.lowerCase(entityKey))}`}
      </div>
    )
  }

  const invokeBulkAction = (moduleId: string, records: unknown[]) => {
    setBulkInvoked(true)
    setBulkModuleId(moduleId)
    setSelectedRecords(records)
  }

  const completeBulkAction = () => {
    setBulkInvoked(false)
    setBulkModuleId(null)
  }

  const clearFiltersFunc = () => {
    setIsCSVDataReady(false)
    clearFiltersProp()
    savedSearchesActions.setSelectedSavedSearch(null)

    _.defer(refreshData)
  }

  const triggerButton = (
    <div data-elm-id='bulk_action_button' className={cx('bulk-trigger')}>
      <Icon
        name='write square'
        size='large'
        data-elm-id='bulk_action_icon'
        color='info'
      />
      <label className={cx('bulk-label')}>Bulk</label>
    </div>
  )

  const renderTableHeader = (data: DataTableRowData[]) => {
    const selectedIds = _.map(
      data.filter((row: DataTableRowData) => row.selected),
      (row: DataTableRowData) => row[primaryKeyField]
    )
    const countSelected = _.size(selectedIds)

    const bulkMenu = !!bulkProcessingModules.length && (
      <div className={cx('bulk-option')}>
        <Dropdown
          disabled={countSelected === 0}
          icon={null}
          trigger={triggerButton}
        >
          <Dropdown.Menu>
            <Dropdown.Header
              data-elm-id='bulk_menu_dropdown_header'
              content={`${formatNumber(countSelected as any)} ${pluralize(
                String(entityName),
                countSelected
              )} Selected`}
            />
            {countSelected > 0 &&
              bulkProcessingModules.map((module: BulkProcessingModule) => (
                <Dropdown.Item
                  key={module.id}
                  data-elm-id={`bulk_menu_item_${_.snakeCase(module.id)}`}
                  onClick={() => invokeBulkAction(module.id, selectedIds)}
                >
                  {module.menuOptionTitle}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    )

    return (
      <div className={cx('data-table-header')}>
        {bulkMenu}
        <div className={cx('content')}>
          {!customTableHeader
            ? renderTableHeaderInfo(data)
            : customTableHeader()}
          <ClauseHolder
            className={cx('clauses')}
            clauses={filterClauses}
            clearFilters={clearFiltersFunc}
            removeFilter={removeFilterFunc}
            onFilterClick={
              searchField.current ? searchField.current.updateColumn : _.noop
            }
          />
        </div>
      </div>
    )
  }

  // Main render
  const tableHeight =
    containerSize.height - (INFOBAR_DEFAULT_HEIGHT + toolbarHeight)

  const bulkProcessingComponent = (props: BulkProcessingComponentProps) => {
    const module =
      bulkProcessingModules.length &&
      _.find(bulkProcessingModules, { id: bulkModuleId })

    if (!module || !module.component) return null
    return module.component(props)
  }

  const standardToolbarProps = {
    allowExportCSV,
    refreshData,
    isLoading,
    enableLocalSearch,
    enableSavedSearches,
    savedSearches,
    filterClauses,
    userAuthorities,
    renderStandardSearch,
    renderLocalSearch,
    renderSavedSearch,
    renderDataTableActions,
  }

  return (
    <div
      className={cx('data-table-container', {
        'data-table-container--fill': fill,
      })}
    >
      <ReactResizeDetector
        handleWidth
        handleHeight
        skipOnMount
        onResize={resizeContainer}
      />
      {renderEditColumnsModal()}
      {renderDownloadModal()}
      <div>
        <ReactResizeDetector
          handleWidth
          handleHeight
          skipOnMount
          onResize={resizeToolbar}
        />
        {CustomToolbar ? (
          <CustomToolbar {...standardToolbarProps} />
        ) : (
          renderToolbar()
        )}
      </div>
      {renderTableHeader(rowData)}
      <Table
        rowsCount={rowData ? rowData.length : 0}
        rowHeight={ROW_HEIGHT}
        headerHeight={ROW_HEIGHT}
        width={containerSize.width}
        height={tableHeight}
        onColumnResizeEndCallback={onColumnResizeEndCallback}
        onScrollEnd={onScrollEnd}
        isColumnResizing={false}
        onColumnReorderEndCallback={onColumnReorderEndCallback}
        isColumnReordering={false}
        rowClassNameGetter={rowClassNameGetter}
        className={cx('data-table')}
        // @ts-expect-error fixed-data-table-2 types expect SyntheticEvent<Table> but handler uses SyntheticEvent<Element>
        onRowClick={onRowClickHandler}
      >
        {columnsState.map((column: ColumnDefinition) =>
          renderColumn(column, rowData, allChecked, isLoading)
        )}
      </Table>
      {bulkProcessingComponent({
        selectedRecords,
        invoked: bulkInvoked,
        onComplete: completeBulkAction,
      })}
    </div>
  )
}

// Performance optimization: Memoize component and prevent re-renders during bulk operations
// This replaces the old shouldComponentUpdate logic from the class component
const MemoizedDataTable = React.memo(DataTable, (_prevProps, _nextProps) => {
  // The shouldComponentUpdate logic was:
  // return !this.state.bulkInvoked || !nextState.bulkInvoked
  // Since bulkInvoked is internal state, we can't replicate it exactly via React.memo on props.
  // Default: always re-render (return false)
  return false
})

export default withApplicationNotificationsContext(MemoizedDataTable)
export {
  DataTable, // for testing
  ActionIcon,
  BulkProcessingModule,
  BulkUpdateDrawer,
  defineTableSchema,
  DownloadDataModal,
  DownloadMenuItem,
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
}
